export const APP_VERSION = '2.1.0';
export const BUILD_ID = '2.1.0';

// Canonical anchor for live clock references
export const APP_NOW_UTC = new Date('2026-05-30T12:00:00Z');

export const formatDateTime = (date) => {
  if (!date) return '';
  return new Date(date).toLocaleString('en-US', {
    timeZone: 'America/New_York',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short'
  });
};

export const formatDate = (date) => {
  if (!date) return '';
  return new Date(date).toLocaleDateString('en-US', {
    timeZone: 'America/New_York',
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
};

export const formatRelative = (date, now = APP_NOW_UTC) => {
  if (!date) return '';
  const diff = now.getTime() - new Date(date).getTime();
  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (days > 0) return `${days}d ago`;
  if (hours > 0) return `${hours}h ago`;
  if (minutes > 0) return `${minutes}m ago`;
  return 'just now';
};

export const resolveDeployedAt = (entry, liveDeployment) => {
  if (liveDeployment && liveDeployment.build_id === entry?.buildId && liveDeployment.deployed_at) {
    return liveDeployment.deployed_at;
  }
  return entry?.deployedAt || entry?.date;
};

export const CHANGELOG_DATA = [
  {
    version: '2.1.0',
    buildId: '2.1.0',
    date: 'May 30, 2026',
    severity: 'high',
    headline: 'Fan Engagement, Partnership Tools & Real-time Analytics',
    summary: 'Major expansion with personalized discovery feeds, vault organization, wishlist functionality, partnership collaboration workspace, and comprehensive real-time analytics.',
    features: [
      {
        title: 'Personalized Discovery Feed',
        description: 'Dynamic homepage feed that prioritizes content from followed artists and matches your favorite genres. Smart relevance scoring based on listening habits.',
        icon: 'FiCompass',
      },
      {
        title: 'Vault Folders & Organization',
        description: 'Create custom folders to organize your unlocked content. Drag-and-drop drops into themed collections for easy access.',
        icon: 'FiFolder',
      },
      {
        title: 'Wishlist Functionality',
        description: 'Save upcoming drops and merch to your wishlist. Get notified when items become available or go on sale.',
        icon: 'FiHeart',
      },
      {
        title: 'Listening History Tracking',
        description: 'Automatic playback telemetry logs your listening activity. View your recent plays and discover patterns in your music taste.',
        icon: 'FiClock',
      },
      {
        title: 'Enhanced Fan Profiles',
        description: 'Expanded profiles showing listening history, recently unlocked content, wishlist items, and engagement analytics with different artists.',
        icon: 'FiUser',
      },
      {
        title: 'Content Versioning',
        description: 'Artists can upload updated versions of exclusive content while preserving version history. Track file changes with hash verification.',
        icon: 'FiFileText',
      },
      {
        title: 'Partnership Collaboration Workspace',
        description: 'Shared file uploads, collaborative milestone tasks, and visual revenue split tracking within partnership management.',
        icon: 'FiUsers',
      },
      {
        title: 'Revenue Split Visualization',
        description: 'Visual representation of partnership revenue splits with percentage tracking and payout monitoring.',
        icon: 'FiPieChart',
      },
      {
        title: 'Artist Activity Feed',
        description: 'Dedicated activity feed showing artist-specific events: new drops, unlocks, follows, and milestone achievements.',
        icon: 'FiActivity',
      },
      {
        title: 'Real-time Metrics Dashboard',
        description: 'Live analytics showing current listeners, active subscribers, unlocks in the last hour, and revenue today. Updates every 30 seconds.',
        icon: 'FiTrendingUp',
      },
      {
        title: 'Advanced Analytics RPCs',
        description: 'Enhanced analytics functions with listener location data, genre breakdowns, and top-performing content identification.',
        icon: 'FiBarChart',
      },
      {
        title: 'Artist Partnerships Table',
        description: 'Core infrastructure for artist collaborations with support for features, split releases, and joint ventures with automatic revenue tracking.',
        icon: 'FiHandshake',
      },
    ],
    fixes: [
      'Fixed missing d2f_artist_partnerships table dependency',
      'Optimized RLS policies for better query performance',
      'Enhanced real-time metrics polling efficiency',
      'Improved listening history telemetry reliability',
    ],
  },
  {
    version: '2.0.0',
    buildId: '2.0.0',
    date: 'May 18, 2026',
    severity: 'high',
    headline: 'Enhanced Analytics, Content Management & Community Features',
    summary: 'Major platform upgrade with advanced artist analytics, content bundling, fan engagement tracking, and admin bulk operations.',
    features: [
      {
        title: 'Enhanced Artist Analytics',
        description: 'Advanced daily performance tracking with interactive charts showing unlocks, revenue trends, and peak days. View metrics for 7, 30, or 90-day periods.',
        icon: 'FiTrendingUp',
      },
      {
        title: 'Milestone Progress Board',
        description: 'Visual Kanban-style board for tracking partnership milestones through planning, in-progress, and completed stages with progress bars.',
        icon: 'FiTarget',
      },
      {
        title: 'Expanded Social Media',
        description: 'Added TikTok, Bandcamp, and YouTube integration with live follower count tracking across all platforms.',
        icon: 'FiUsers',
      },
      {
        title: 'Content Tagging System',
        description: 'Comprehensive tagging for drops with genre, mood, theme, and custom categories. Improve discoverability with filterable tags.',
        icon: 'FiTag',
      },
      {
        title: 'Content Bundles',
        description: 'Bundle multiple drops into discounted packages. Artists can create themed collections with automatic discount calculation.',
        icon: 'FiPackage',
      },
      {
        title: 'Enhanced Pre-orders',
        description: 'Set pre-order bonuses and limited quantities for drops. Track pre-order progress with dedicated limits and incentives.',
        icon: 'FiClock',
      },
      {
        title: 'Fan Engagement Stats',
        description: 'Detailed fan profiles showing total unlocks, spending, favorite genre, and top artist. Track engagement metrics over time.',
        icon: 'FiHeart',
      },
      {
        title: 'DM Enhancements',
        description: 'Added read receipts, typing indicators, and pinned conversations for better messaging experience.',
        icon: 'FiMessage',
      },
      {
        title: 'Artist Shoutouts',
        description: 'Send public shoutouts to top supporters and fans directly from your dashboard. Appears in activity feed.',
        icon: 'FiMegaphone',
      },
      {
        title: 'Admin Bulk Actions',
        description: 'Platform admins can now perform bulk publish, unpublish, delete, and role updates with operation logging.',
        icon: 'FiShield',
      },
      {
        title: 'Automated Payouts Framework',
        description: 'Foundation laid for Stripe-integrated automated payouts based on completed transactions and thresholds.',
        icon: 'FiDollarSign',
      },
    ],
    fixes: [
      'Improved RLS policies for better performance',
      'Enhanced error handling in approval workflows',
      'Fixed notification delivery for rejected applications',
      'Optimized analytics query performance',
    ],
  },
  {
    version: '1.5.0',
    buildId: '1.5.0',
    date: 'May 10, 2026',
    severity: 'medium',
    headline: 'Real-time Activity Feeds, DMs & Advanced Analytics',
    summary: 'Social layer implementation with activity feeds, gated direct messaging, and comprehensive analytics dashboard.',
    features: [
      {
        title: 'Activity Feed',
        description: 'Global and personalized activity feeds showing unlocks, follows, milestones, and shoutouts.',
        icon: 'FiActivity',
      },
      {
        title: 'Direct Messaging',
        description: 'Gated DM system between artists and fans with eligibility checks based on unlocks/subscriptions.',
        icon: 'FiMessage',
      },
      {
        title: 'Promo Codes',
        description: 'Artist-created discount codes with percentage, fixed, or free access options.',
        icon: 'FiTag',
      },
      {
        title: 'Pre-orders',
        description: 'Fans can pre-order scheduled drops with notifications on release.',
        icon: 'FiCalendar',
      },
      {
        title: 'Drop Reactions & Comments',
        description: 'Emoji reactions and comment threads on drops for community engagement.',
        icon: 'FiHeart',
      },
    ],
    fixes: [
      'Fixed RLS recursion issues in profile queries',
      'Improved JWT-based role checking',
      'Enhanced security with rate limiting',
    ],
  },
  {
    version: '1.0.0',
    buildId: '1.0.0',
    date: 'April 1, 2026',
    severity: 'high',
    headline: 'Platform Launch',
    summary: 'Initial release of Vision Direct - connecting artists directly with fans.',
    features: [
      {
        title: 'Artist Profiles',
        description: 'Complete artist profiles with discography, merch, and subscription tiers.',
        icon: 'FiUser',
      },
      {
        title: 'Drop System',
        description: 'Exclusive content drops with PWYW, limited editions, and scheduled releases.',
        icon: 'FiMusic',
      },
      {
        title: 'Fan Vaults',
        description: 'Personal vault for fans to access all unlocked content in one place.',
        icon: 'FiLock',
      },
      {
        title: 'Subscription Tiers',
        description: 'Monthly subscription tiers with exclusive benefits for supporters.',
        icon: 'FiStar',
      },
      {
        title: 'Partnership System',
        description: 'Artist collaborations with milestone tracking and revenue sharing.',
        icon: 'FiHandshake',
      },
    ],
    fixes: [],
  },
];

export const CHANGELOG_ENTRIES = CHANGELOG_DATA.map(entry => {
  const items = [];
  if (entry.features) {
    entry.features.forEach(f => {
      items.push({
        title: f.title,
        category: 'feature',
        emoji: '🚀',
        body: f.description,
        deployedAt: entry.date
      });
    });
  }
  if (entry.fixes) {
    entry.fixes.forEach(f => {
      items.push({
        title: 'Fix',
        category: 'fix',
        emoji: '🐛',
        body: f,
        deployedAt: entry.date
      });
    });
  }
  return {
    ...entry,
    deployedAt: entry.date,
    items
  };
});

export const getLatestChangelog = () => CHANGELOG_DATA[0];

export const getLatestEntry = () => CHANGELOG_ENTRIES[0];

export const getChangelogByVersion = (version) => CHANGELOG_DATA.find(c => c.version === version);

export const getRandomToast = (entry) => {
  const toasts = [
    `New features are here! ${entry?.headline || ''}`,
    `System updated to version ${entry?.version || APP_VERSION}.`,
    `We just released an update! Checkout what's new.`,
  ];
  return toasts[Math.floor(Math.random() * toasts.length)];
};